import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MnemonicVerify } from '@/components/MnemonicVerify';
import { MnemonicGrid } from '@/components/MnemonicGrid';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { passwordStrength } from '@/lib/validate';

interface BackupLocationState {
  words?: string[];
}

/**
 * 助记词备份页：**逐词验证（★ 不可跳过）** + 设置口令。
 * 未通过验证 → 绝对不进入下一步（PRD §9.3 / ARCH T02.6）。
 */
export function BackupPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as BackupLocationState;
  const words = useMemo(() => state.words ?? [], [state.words]);

  const setUnlocked = useIdentityStore((s) => s.setUnlocked);
  const pushError = useUiStore((s) => s.pushError);
  const pushToast = useUiStore((s) => s.pushToast);

  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const strength = passwordStrength(password);
  const passwordOk = password.length >= 6 && password === confirm;

  const submit = async (): Promise<void> => {
    if (!verified || !passwordOk) return;
    setSubmitting(true);
    try {
      const meta = await container.identity.verifyAndSeal(words, password);
      setUnlocked(meta);
      pushToast('success', '身份已创建，DID 注册与水龙头已尝试提交');
      navigate('/chat', { replace: true });
    } catch (err) {
      pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setSubmitting(false);
      setPassword('');
      setConfirm('');
    }
  };

  if (words.length === 0) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div className="card w-[min(92vw,460px)] p-6 text-center">
          <p className="text-sm text-dim">没有待备份的助记词，请从创建流程进入。</p>
          <Button className="mt-4" onClick={() => navigate('/create')}>
            去创建身份
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="card w-[min(92vw,520px)] p-6">
        <h1 className="text-lg font-semibold text-text">验证助记词</h1>
        <p className="mt-1 text-sm text-dim">请按顺序点选刚才抄写的 12 个词。此步骤不可跳过。</p>

        {!verified ? (
          <div className="mt-5">
            <MnemonicVerify words={words} onVerified={() => setVerified(true)} />
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="rounded-btn border border-ok/40 bg-ok/10 px-3 py-2 text-xs text-ok">
              验证通过 ✓ 请设置解锁口令（口令用于加密本机私钥，无法找回）。
            </div>
            <MnemonicGrid words={words} masked copyable={false} />
            <Input
              type="password"
              placeholder="设置口令（至少 6 位）"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint={password ? `强度：${'●'.repeat(Math.max(1, strength))}${'○'.repeat(Math.max(0, 4 - strength))}` : undefined}
            />
            <Input
              type="password"
              placeholder="再次输入口令"
              value={confirm}
              invalid={confirm.length > 0 && password !== confirm}
              hint={confirm.length > 0 && password !== confirm ? '两次输入不一致' : undefined}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <Button block loading={submitting} disabled={!passwordOk} onClick={() => void submit()}>
              加密保存并进入
            </Button>
          </div>
        )}

        {!verified ? (
          <div className="mt-5 flex justify-start">
            <Button variant="ghost" size="sm" onClick={() => navigate('/create')}>
              返回重新生成
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default BackupPage;
